import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import {
  API,
  cn,
  DEPARTMENT_COLORS,
  STATUS_COLORS,
  DEPARTMENT_LABELS,
  STATUS_LABELS,
  formatDate,
  getDaysUntil,
  isOverdue,
  isToday,
} from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ClipboardList,
  CheckCircle2,
  Clock,
  AlertTriangle,
  TrendingUp,
  Calendar,
  FileCheck,
  Package,
  Eye,
  Image as ImageIcon,
} from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [openPOs, setOpenPOs] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [orderTaskSummary, setOrderTaskSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/dashboard/tasks-by-department`, { withCredentials: true }),
          axios.get(`${API}/dashboard/my-pending-tasks`, { withCredentials: true }),
          axios.get(`${API}/orders/open-pos`, { withCredentials: true }),
          axios.get(`${API}/dashboard/order-task-summary`, { withCredentials: true }),
        ];

        // Admin-specific data
        if (isAdmin) {
          promises.push(axios.get(`${API}/orders/pending-approval`, { withCredentials: true }));
        }

        const results = await Promise.all(promises);

        setStats(results[0].data);
        setDepartmentStats(results[1].data);
        setPendingTasks(results[2].data);
        setOpenPOs(results[3].data);
        setOrderTaskSummary(results[4].data);
        
        if (isAdmin && results[5]) {
          setPendingApprovals(results[5].data);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAdmin]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Welcome back, {user?.name?.split(" ")[0]}
            {stats?.user_department && (
              <Badge variant="outline" className="ml-2 capitalize">
                {DEPARTMENT_LABELS[stats.user_department] || stats.user_department}
              </Badge>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/orders/new">
            <Button className="rounded-sm" data-testid="new-order-button">
              New Order
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
        <StatCard
          title="Active Orders"
          value={stats?.active_orders || 0}
          icon={ClipboardList}
          color="text-primary"
          onClick={() => navigate("/orders?status=active")}
        />
        {isAdmin && (
          <StatCard
            title="Pending Approval"
            value={stats?.pending_approval || 0}
            icon={FileCheck}
            color="text-violet-500"
            highlight={stats?.pending_approval > 0}
            onClick={() => navigate("/orders?status=pending_approval")}
          />
        )}
        <StatCard
          title="Due Today"
          value={stats?.due_today_tasks || 0}
          icon={Calendar}
          color="text-amber-500"
          onClick={() => navigate("/tasks?due_today=true")}
        />
        <StatCard
          title="Overdue"
          value={stats?.overdue_tasks || 0}
          icon={AlertTriangle}
          color="text-rose-500"
          highlight={stats?.overdue_tasks > 0}
          onClick={() => navigate("/tasks?overdue=true")}
        />
        <StatCard
          title="On-Time Rate"
          value={`${stats?.on_time_rate || 0}%`}
          icon={TrendingUp}
          color="text-emerald-500"
          showProgress
          progressValue={stats?.on_time_rate || 0}
        />
      </div>

      {/* Main Content - Tabs */}
      <Tabs defaultValue="order-tasks" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="order-tasks" className="rounded-sm">
            Order Tasks
          </TabsTrigger>
          <TabsTrigger value="pending-tasks" className="rounded-sm">
            My Pending ({pendingTasks.length})
          </TabsTrigger>
          <TabsTrigger value="open-pos" className="rounded-sm">
            Open POs ({openPOs.length})
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="approvals" className="rounded-sm">
              Approvals ({pendingApprovals.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="departments" className="rounded-sm">
            Departments
          </TabsTrigger>
        </TabsList>

        {/* Order Tasks Tabular View */}
        <TabsContent value="order-tasks">
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-xl">Order Task Summary</CardTitle>
              <CardDescription>Department-wise task status for each active order</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {orderTaskSummary.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No active orders with tasks</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Order No</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Customer</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Progress</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Merchandizing</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Prepress</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Production</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Logistics</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orderTaskSummary.map((row) => (
                        <TableRow
                          key={row.order_id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/orders/${row.order_id}`)}
                          data-testid={`order-task-row-${row.order_id}`}
                        >
                          <TableCell className="font-mono font-medium whitespace-nowrap">{row.order_number}</TableCell>
                          <TableCell className="whitespace-nowrap">{row.customer_name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 min-w-[100px]">
                              <Progress value={row.progress} className="h-1.5 flex-1" />
                              <span className="text-xs font-mono font-medium">{row.progress}%</span>
                            </div>
                          </TableCell>
                          {["merchandizing", "prepress", "production", "logistics"].map((dept) => (
                            <TableCell key={dept}>
                              <DeptStatusCell stats={row.departments[dept]} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Tasks Tab */}
        <TabsContent value="pending-tasks">
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-xl">My Pending Tasks</CardTitle>
              <CardDescription>Tasks assigned to you or your department</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border">
                {pendingTasks.slice(0, 10).map((task) => (
                  <TaskRow key={task.task_id} task={task} />
                ))}
                {pendingTasks.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No pending tasks
                  </p>
                )}
              </div>
              {pendingTasks.length > 10 && (
                <div className="mt-4 text-center">
                  <Link to="/tasks">
                    <Button variant="outline" size="sm">
                      View All {pendingTasks.length} Tasks
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Open POs Tab */}
        <TabsContent value="open-pos">
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-xl">Open Purchase Orders</CardTitle>
              <CardDescription>Active orders with item and task progress</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {openPOs.map((order) => (
                  <OpenPOCard key={order.order_id} order={order} />
                ))}
                {openPOs.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No open purchase orders
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approvals Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="approvals">
            <Card className="border border-border shadow-none rounded-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-xl">Pending Approvals</CardTitle>
                <CardDescription>Orders waiting for your approval</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingApprovals.map((order) => (
                    <ApprovalCard key={order.order_id} order={order} />
                  ))}
                  {pendingApprovals.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No orders pending approval
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Departments Tab */}
        <TabsContent value="departments">
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-xl">Department Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {departmentStats.map((dept) => (
                  <DepartmentRow key={dept.department} data={dept} />
                ))}
                {departmentStats.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No department data available
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, showProgress, progressValue, highlight, onClick }) => (
  <Card 
    className={cn(
      "border border-border shadow-none rounded-sm transition-colors",
      onClick && "cursor-pointer hover:bg-muted/50",
      highlight && "border-amber-300 bg-amber-50/50"
    )}
    onClick={onClick}
    data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            {title}
          </p>
          <p className="metric-value mt-2">{value}</p>
        </div>
        <div className={cn("p-2 rounded-sm bg-muted", color)}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
      </div>
      {showProgress && (
        <Progress value={progressValue} className="mt-3 h-1" />
      )}
    </CardContent>
  </Card>
);

// Task Row Component with Item Image
const TaskRow = ({ task }) => {
  const navigate = useNavigate();
  const deptColors = DEPARTMENT_COLORS[task.department] || DEPARTMENT_COLORS.merchandizing;
  const daysUntil = getDaysUntil(task.target_date);
  const overdue = isOverdue(task.target_date);
  const today = isToday(task.target_date);

  return (
    <div
      onClick={() => navigate(`/tasks/${task.task_id}`)}
      className="flex items-center gap-4 py-3 hover:bg-muted/50 -mx-6 px-6 transition-colors cursor-pointer"
      data-testid={`task-row-${task.task_id}`}
    >
      {/* Item Image */}
      <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
        {task.item_image_url ? (
          <img src={task.item_image_url} alt={task.item_name} className="w-full h-full object-cover" />
        ) : (
          <Package className="w-5 h-5 text-muted-foreground" />
        )}
      </div>
      
      <div className={cn("w-1 h-10 rounded-full flex-shrink-0", deptColors.bg)} />
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{task.task_name}</p>
        <p className="text-xs text-muted-foreground truncate">
          {task.order_number} • {task.item_name}
        </p>
      </div>
      
      <div className="text-right flex-shrink-0">
        <Badge
          variant="secondary"
          className={cn(
            "font-mono text-xs",
            overdue && "bg-rose-50 text-rose-700",
            today && !overdue && "bg-amber-50 text-amber-700"
          )}
        >
          {overdue ? `${Math.abs(daysUntil)}d overdue` : today ? "Today" : `${daysUntil}d`}
        </Badge>
      </div>
    </div>
  );
};

// Open PO Card Component
const OpenPOCard = ({ order }) => {
  const navigate = useNavigate();
  const completionRate = order.total_tasks > 0 
    ? Math.round((order.total_completed / order.total_tasks) * 100) 
    : 0;

  return (
    <Card 
      className="border border-border shadow-none rounded-sm cursor-pointer hover:bg-muted/30 transition-colors"
      onClick={() => navigate(`/orders/${order.order_id}`)}
      data-testid={`open-po-${order.order_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="font-mono font-medium">{order.order_number}</span>
            <span className="text-muted-foreground mx-2">•</span>
            <span>{order.customer_name}</span>
          </div>
          <Badge 
            variant="secondary" 
            className={cn(
              "font-mono text-xs",
              order.status === "active" && "bg-emerald-50 text-emerald-700",
              order.status === "pending_approval" && "bg-violet-50 text-violet-700"
            )}
          >
            {order.status === "pending_approval" ? "Pending Approval" : order.status}
          </Badge>
        </div>
        
        {/* Items with progress */}
        <div className="space-y-2">
          {Object.entries(order.items_progress || {}).map(([itemId, itemData]) => (
            <div key={itemId} className="flex items-center gap-3 p-2 bg-muted/30 rounded-sm">
              {/* Item Image */}
              <div className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                {itemData.image_url ? (
                  <img src={itemData.image_url} alt={itemData.item_name} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{itemData.item_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Progress 
                    value={itemData.total_tasks > 0 ? (itemData.completed_tasks / itemData.total_tasks) * 100 : 0} 
                    className="h-1.5 flex-1" 
                  />
                  <span className="text-xs font-mono text-muted-foreground">
                    {itemData.completed_tasks}/{itemData.total_tasks}
                  </span>
                </div>
              </div>
              
              <div className="flex gap-1">
                {itemData.delayed_tasks > 0 && (
                  <Badge variant="destructive" className="text-xs">{itemData.delayed_tasks} delayed</Badge>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Overall Progress */}
        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Overall Progress</span>
          <div className="flex items-center gap-2">
            <Progress value={completionRate} className="w-24 h-1.5" />
            <span className="text-xs font-mono">{completionRate}%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Approval Card Component (Admin)
const ApprovalCard = ({ order }) => {
  const navigate = useNavigate();

  return (
    <Card 
      className="border border-violet-200 bg-violet-50/30 shadow-none rounded-sm cursor-pointer hover:bg-violet-50/50 transition-colors"
      onClick={() => navigate(`/orders/${order.order_id}`)}
      data-testid={`approval-${order.order_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-mono font-medium">{order.order_number}</span>
            <span className="text-muted-foreground mx-2">•</span>
            <span>{order.customer_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="font-mono text-xs">
              {order.items?.length || 0} items
            </Badge>
            <Button size="sm" className="rounded-sm h-7">
              <Eye className="w-3 h-3 mr-1" />
              Review
            </Button>
          </div>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Created: {formatDate(order.created_at)} • Delivery: {formatDate(order.target_delivery_date)}
        </div>
      </CardContent>
    </Card>
  );
};

// Department Status Cell for Tabular Dashboard
const DeptStatusCell = ({ stats }) => {
  if (!stats) return <span className="text-muted-foreground text-xs">-</span>;
  const total = stats.in_progress + stats.overdue + stats.pending + stats.completed;
  if (total === 0) return <span className="text-muted-foreground text-xs">-</span>;
  return (
    <div className="space-y-0.5 text-xs min-w-[100px]" data-testid="dept-status-cell">
      {stats.in_progress > 0 && (
        <div className="text-amber-600 font-medium">{stats.in_progress} In Process</div>
      )}
      {stats.overdue > 0 && (
        <div className="text-rose-600 font-medium">{stats.overdue} Overdue</div>
      )}
      {stats.pending > 0 && (
        <div className="text-foreground">{stats.pending} Pending</div>
      )}
      {stats.completed > 0 && (
        <div className="text-emerald-600 font-medium">{stats.completed} Completed</div>
      )}
    </div>
  );
};

// Department Row Component
const DepartmentRow = ({ data }) => {
  const colors = DEPARTMENT_COLORS[data.department] || DEPARTMENT_COLORS.merchandizing;
  const completionRate = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;

  return (
    <div className={cn("p-4 border rounded-sm border-l-4", colors.border)}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-medium capitalize">
          {DEPARTMENT_LABELS[data.department] || data.department}
        </span>
        <Badge variant="secondary" className="font-mono text-xs">
          {data.total} tasks
        </Badge>
      </div>
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-2xl font-heading">{data.pending}</p>
          <p className="text-xs text-muted-foreground">Pending</p>
        </div>
        <div>
          <p className="text-2xl font-heading">{data.in_progress}</p>
          <p className="text-xs text-muted-foreground">In Progress</p>
        </div>
        <div>
          <p className="text-2xl font-heading text-emerald-500">{data.completed}</p>
          <p className="text-xs text-muted-foreground">Completed</p>
        </div>
      </div>
      <Progress value={completionRate} className="mt-3 h-1" />
    </div>
  );
};

// Dashboard Skeleton
const DashboardSkeleton = () => (
  <div className="space-y-8">
    <div>
      <Skeleton className="h-10 w-48" />
      <Skeleton className="h-5 w-64 mt-2" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className="h-28" />
      ))}
    </div>
    <Skeleton className="h-96" />
  </div>
);
